import type { FeatureFlagsProvider } from './feature-flags'

export interface LaunchDarklyConfig {
  clientSideId: string
  user?: LaunchDarklyUser
  options?: LaunchDarklyOptions
  fallbackToLocalStorage?: boolean
}

export interface LaunchDarklyUser {
  key: string
  email?: string
  name?: string
  custom?: Record<string, unknown>
}

export interface LaunchDarklyOptions {
  streaming?: boolean
  bootstrap?: Record<string, unknown>
  evaluationReasons?: boolean
  sendEvents?: boolean
  allAttributesPrivate?: boolean
}

interface LDClientInstance {
  waitUntilReady(): Promise<void>
  allFlags(): Record<string, unknown>
  on(event: 'change', cb: (changes: Record<string, { current: unknown }>) => void): void
  variation(key: string, defaultVal: unknown): unknown
  identify(user: LaunchDarklyUser): Promise<void>
  track(eventName: string, data?: unknown): void
  flush(): Promise<void>
  close(): Promise<void>
}

interface LDClientModule {
  initialize(clientSideId: string, user: LaunchDarklyUser, options: Record<string, unknown>): LDClientInstance
}

export class LaunchDarklyProvider implements FeatureFlagsProvider {
  private ldClient: LDClientInstance | null = null
  private flags: Map<string, boolean> = new Map()
  private listeners: Set<(flags: Record<string, boolean>) => void> = new Set()
  private isReady = false
  private fallbackProvider?: FeatureFlagsProvider

  constructor(private config: LaunchDarklyConfig, fallbackProvider?: FeatureFlagsProvider) {
    this.fallbackProvider = fallbackProvider
    void this.initialize()
  }

  private async initialize(): Promise<void> {
    try {
      const LDClient = await this.loadLaunchDarklySdk()
      if (!LDClient) {
        console.warn('[LaunchDarkly] SDK not available, using fallback')
        return
      }

      const user = this.config.user || { key: this.generateAnonymousKey(), anonymous: true } as any

      this.ldClient = LDClient.initialize(this.config.clientSideId, user, {
        streaming: this.config.options?.streaming ?? true,
        bootstrap: this.config.options?.bootstrap,
        evaluationReasons: this.config.options?.evaluationReasons ?? false,
        sendEvents: this.config.options?.sendEvents ?? true,
        allAttributesPrivate: this.config.options?.allAttributesPrivate ?? false,
      })

      await this.ldClient.waitUntilReady()
      this.isReady = true

      const allFlags = this.ldClient.allFlags()
      Object.entries(allFlags).forEach(([key, value]) => this.flags.set(key, Boolean(value)))
      this.ldClient.on('change', (changes) => {
        Object.entries(changes).forEach(([key, change]) => this.flags.set(key, Boolean(change.current)))
        this.notifyListeners()
      })
      this.notifyListeners()
    } catch (error) {
      console.error('[LaunchDarkly] Initialization failed:', error)
    }
  }

  private async loadLaunchDarklySdk(): Promise<LDClientModule | null> {
    try {
      // Note: Keep SDK optional; load dynamically
      try {
        // Avoid static resolution by bundlers to keep SDK optional
        const name = 'launchdarkly-js-client-sdk'
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - vite-ignore prevents pre-bundling
        const sdk: any = await import(/* @vite-ignore */ (name as any)).catch(() => null)
        if (!sdk) {
          console.warn('[LaunchDarkly] SDK not installed. Run: npm install launchdarkly-js-client-sdk')
          return null
        }
        return (sdk as { default?: LDClientModule }).default || (sdk as LDClientModule)
      } catch {
        console.warn('[LaunchDarkly] SDK not installed. Run: npm install launchdarkly-js-client-sdk')
        return null
      }
    } catch (error) {
      console.error('[LaunchDarkly] Failed to load SDK:', error)
      return null
    }
  }

  private generateAnonymousKey(): string {
    const storageKey = 'ld_anonymous_key'
    let key = localStorage.getItem(storageKey)
    if (!key) {
      key = `anonymous_${Math.random().toString(36).substring(2)}`
      localStorage.setItem(storageKey, key)
    }
    return key
  }

  getFlag(key: string): boolean {
    if (this.isReady && this.ldClient) {
      try {
        return Boolean(this.ldClient.variation(key, false))
      } catch (e) {
        console.error(`[LaunchDarkly] Error getting flag ${key}:`, e)
      }
    }
    if (this.flags.has(key)) return this.flags.get(key)!
    if (this.fallbackProvider) return this.fallbackProvider.getFlag(key)
    return false
  }

  getAllFlags(): Record<string, boolean> {
    if (this.isReady && this.ldClient) {
      try {
        const all = this.ldClient.allFlags()
        return Object.fromEntries(Object.entries(all).map(([k, v]) => [k, Boolean(v)]))
      } catch (e) {
        console.error('[LaunchDarkly] Error getting all flags:', e)
      }
    }
    if (this.flags.size > 0) return Object.fromEntries(this.flags)
    if (this.fallbackProvider) return this.fallbackProvider.getAllFlags()
    return {}
  }

  setFlag(key: string, enabled: boolean): void {
    this.flags.set(key, enabled)
    if (this.fallbackProvider) this.fallbackProvider.setFlag(key, enabled)
    this.notifyListeners()
  }

  subscribe(listener: (flags: Record<string, boolean>) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notifyListeners(): void {
    const flags = this.getAllFlags()
    this.listeners.forEach(l => l(flags))
  }

  async identify(user: LaunchDarklyUser): Promise<void> {
    if (!this.isReady || !this.ldClient) return
    try {
      await this.ldClient.identify(user)
      const all = this.ldClient.allFlags()
      Object.entries(all).forEach(([k, v]) => this.flags.set(k, Boolean(v)))
      this.notifyListeners()
    } catch (e) {
      console.error('[LaunchDarkly] Failed to identify user:', e)
    }
  }

  track(eventName: string, data?: unknown): void {
    if (!this.isReady || !this.ldClient) return
    try { this.ldClient.track(eventName, data) } catch (e) { console.error('[LaunchDarkly] track failed:', e) }
  }

  async flush(): Promise<void> { if (this.isReady && this.ldClient) { try { await this.ldClient.flush() } catch {} } }
  async close(): Promise<void> { if (this.isReady && this.ldClient) { try { await this.ldClient.close() } catch {} } }
}

export default LaunchDarklyProvider
